// components/landing/AiFeatureSection.js
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { 
  FiClock, 
  FiZap, 
  FiUserCheck, 
  FiMessageSquare, 
  FiLayers, 
  FiActivity, 
  FiShield, 
  FiTrendingUp, 
  FiCheck,
  FiArrowRight,
  FiCpu
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import aiWhatsappAgentImg from '../../assets/imagens/ai_whatsapp_agent.png';

const AiFeatureSection = () => {
  const benefits = [
    { title: "Atendimento 24/7", Icon: FiClock, desc: "Nunca perca um cliente, mesmo fora do horário comercial." },
    { title: "Respostas instantâneas", Icon: FiZap, desc: "Menos de 2 segundos de tempo de resposta com IA." },
    { title: "Captação automática de leads", Icon: FiUserCheck, desc: "IA salva contatos, nomes e e-mails automaticamente." },
    { title: "Integração com WhatsApp", Icon: FiMessageSquare, desc: "Conexão direta com a API do seu WhatsApp Business." },
    { title: "Atendimento simultâneo", Icon: FiLayers, desc: "Centenas de conversas ao mesmo tempo, sem sobrecarga." },
    { title: "Transbordo Humano", Icon: FiActivity, desc: "Encaminhe para atendentes humanos quando necessário." },
    { title: "Histórico completo", Icon: FiShield, desc: "Acompanhe todas as interações e relatórios em tempo real." },
    { title: "Aumento de conversões", Icon: FiTrendingUp, desc: "Nutrição ativa de leads para fechar mais vendas." }
  ];

  // WhatsApp Conversation Mockup sequence ending with user affirmation
  const chatMessages = [
    { id: 1, sender: 'user', text: 'Vocês realmente transformam o meu site em aplicativo?' },
    { id: 2, sender: 'ai', text: 'Sim! De forma rápida e prática. Você só precisa informar a URL do seu site, enviar o logotipo/ícone e nós cuidamos de toda a transformação.' },
    { id: 3, sender: 'user', text: 'Que excelente! E quanto à publicação nas lojas?' },
    { id: 4, sender: 'ai', text: 'Nossa plataforma faz a publicação automatizada na Google Play e App Store. Seu app fica pronto em minutos e com suporte integrado! 🚀' },
    { id: 5, sender: 'user', text: 'Que top! eu quero.' },
    { id: 6, sender: 'ai', text: 'Perfeito! Clique no botão \'Quero Meu App com IA\' para começar agora mesmo! 🚀' }
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const chatContainerRef = useRef(null);

  // Auto-scroll inside chat box when messages update
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
         top: chatContainerRef.current.scrollHeight,
         behavior: 'smooth'
      });
    }
  }, [currentStep]);

  // Clean, strict-mode safe sequence controller
  useEffect(() => {
    let active = true;
    const runSequence = async () => {
      if (!active) return;
      setCurrentStep(0);
      
      const steps = [
        { step: 1, delay: 1200 }, // User 1 appears
        { step: 2, delay: 1200 }, // AI 1 starts typing
        { step: 3, delay: 1800 }, // AI 1 appears
        { step: 4, delay: 2400 }, // User 2 appears
        { step: 5, delay: 1200 }, // AI 2 starts typing
        { step: 6, delay: 2000 }, // AI 2 appears
        { step: 7, delay: 2200 }, // User 3 appears ("Que top! eu quero.")
        { step: 8, delay: 1200 }, // AI 3 starts typing
        { step: 9, delay: 1800 }  // AI 3 appears ("Perfeito!...")
      ];

      for (const s of steps) {
        await new Promise(resolve => setTimeout(resolve, s.delay));
        if (!active) return;
        setCurrentStep(s.step);
      }
      
      await new Promise(resolve => setTimeout(resolve, 6000));
      if (!active) return;
      runSequence();
    };
    
    runSequence();
    return () => {
      active = false;
    };
  }, []);

  const getVisibleMessages = () => {
    const msgs = [];
    if (currentStep >= 1) msgs.push(chatMessages[0]);
    if (currentStep >= 3) msgs.push(chatMessages[1]);
    if (currentStep >= 4) msgs.push(chatMessages[2]);
    if (currentStep >= 6) msgs.push(chatMessages[3]);
    if (currentStep >= 7) msgs.push(chatMessages[4]);
    if (currentStep >= 9) msgs.push(chatMessages[5]);
    return msgs;
  };
  
  const isAiTyping = currentStep === 2 || currentStep === 5 || currentStep === 8;

  return (
    <section id="ai-whatsapp-section" className="py-24 bg-gradient-to-b from-[#f4fcf8] via-[#d7f5e3] to-[#f4fcf8] relative overflow-hidden">
      {/* Hide scrollbar styles */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div className="absolute top-1/4 left-0 w-72 h-72 bg-emerald-500/8 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#25d366]/8 blur-[150px] rounded-full pointer-events-none"></div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        
        {/* Main Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-20">
          
          {/* Lado Esquerdo - Imagem da Mulher Corporativa + Chat WhatsApp Animado */}
          <div className="lg:col-span-6 relative flex justify-center">
            <div className="relative max-w-md md:max-w-lg w-full aspect-square md:aspect-[4/5] rounded-3xl overflow-visible">
              
              {/* Main Portrait Image */}
              <div className="w-full h-full rounded-3xl overflow-hidden shadow-2xl bg-slate-50 relative group">
                <img 
                  src={aiWhatsappAgentImg} 
                  alt="Atendimento Inteligente com IA" 
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/10 to-transparent"></div>
              </div>

              {/* Glowing decorative indicator */}
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-blue-500/10 rounded-full blur-xl animate-pulse"></div>
              
              {/* WhatsApp Floating Overlay (Premium Glassmorphic Box with Spring Entrance) */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, x: 20 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 100, damping: 15, delay: 0.3 }}
                className="absolute -right-2 md:-right-6 bottom-6 max-w-[220px] sm:max-w-[260px] w-full z-20 flex flex-col gap-2 font-sans"
              >
                
                {/* Active Support Header */}
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/95 backdrop-blur-md p-2.5 rounded-2xl border border-gray-100 shadow-lg flex items-center gap-2.5"
                >
                  <div className="relative flex items-center justify-center bg-emerald-500 text-white w-8 h-8 rounded-xl shadow-md shadow-emerald-500/20 shrink-0">
                    <FaWhatsapp className="w-4.5 h-4.5" />
                    <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs font-bold text-gray-900 leading-tight truncate">Suporte IA</h4>
                    <p className="text-[9px] text-emerald-600 flex items-center gap-1 font-medium leading-none">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                      Online • Resposta Instantânea
                    </p>
                  </div>
                </motion.div>

                {/* Messages Container (Fixed height to prevent resizing layout shifts) */}
                <div className="bg-slate-50/90 backdrop-blur-md p-3 rounded-2xl border border-gray-100 shadow-xl flex flex-col gap-2 h-[260px] justify-end overflow-hidden relative">
                  
                  {/* Fade mask overlay to smoothly hide scrolled-up messages */}
                  <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-slate-50/95 via-slate-50/70 to-transparent pointer-events-none z-10 rounded-t-2xl"></div>

                  <div ref={chatContainerRef} className="overflow-y-auto no-scrollbar flex flex-col gap-2.5 pr-0.5 max-h-full relative z-0">
                    <AnimatePresence mode="popLayout">
                      {getVisibleMessages().map((msg) => (
                        <motion.div
                          layout
                          key={msg.id}
                          initial={{ opacity: 0, y: 15, scale: 0.92 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ 
                            type: "spring",
                            stiffness: 180,
                            damping: 15
                          }}
                          className={`p-2.5 rounded-2xl text-[10px] sm:text-[11px] leading-snug shadow-sm flex flex-col relative ${
                            msg.sender === 'user'
                              ? 'bg-blue-600 text-white rounded-tr-none self-end'
                              : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none self-start'
                          }`}
                        >
                          <span className="pb-1">{msg.text}</span>
                          
                          {/* Premium Whatsapp Details - Timestamp & Double Checkmark */}
                          <div className={`text-[8px] self-end mt-0.5 flex items-center gap-1 font-light ${
                            msg.sender === 'user' ? 'text-blue-200' : 'text-gray-400'
                          }`}>
                            <span>Agora</span>
                            {msg.sender === 'user' && (
                              <span className="text-blue-100 font-bold flex">
                                <FiCheck className="w-2.5 h-2.5 -mr-1" />
                                <FiCheck className="w-2.5 h-2.5 text-sky-300" />
                              </span>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {/* Typing Indicator */}
                    {isAiTyping && (
                      <motion.div 
                        layout
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white text-gray-500 border border-gray-100 p-2.5 rounded-2xl rounded-tl-none text-[10px] self-start flex items-center gap-1.5 shadow-sm shrink-0"
                      >
                        <span className="text-[8px] font-medium text-gray-400">IA digitando</span>
                        <div className="flex gap-0.5">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>

            </div>
          </div>

          {/* Lado Direito - Título, Descrição, Benefícios e Destaque */}
          <div className="lg:col-span-6 flex flex-col justify-center text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-100/50 text-emerald-600 text-xs font-semibold uppercase tracking-wider mb-6">
                <FiCpu className="w-3.5 h-3.5" />
                Novidade: Inteligência Artificial
              </div>

              {/* Headings */}
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-4 leading-tight">
                Atendimento com IA no WhatsApp <span className="text-emerald-600 block sm:inline">24 Horas por Dia</span>
              </h2>
              <h3 className="text-lg font-medium text-gray-700 mb-5 leading-relaxed">
                Seu atendimento e suporte inteligente sempre ativos: nossa IA responde a qualquer dúvida e consulta instantaneamente.
              </h3>
              <p className="text-gray-600 font-light leading-relaxed mb-8">
                Automatize respostas, captação de leads, suporte, vendas e acompanhamento de clientes utilizando Inteligência Artificial integrada ao WhatsApp.
              </p>
            </motion.div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex gap-3 items-start p-2 hover:bg-slate-50/50 rounded-xl transition-all">
                  <div className="mt-0.5 text-emerald-600 bg-emerald-50 p-2 rounded-lg border border-emerald-100/30">
                    <benefit.Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 leading-none mb-1">{benefit.title}</h4>
                    <p className="text-xs text-gray-500 leading-tight font-light">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mt-6 w-full">
              <Link to="/login" className="w-full sm:w-auto">
                <button className="w-full sm:w-auto px-6 py-2.5 bg-black text-white rounded-none border border-black hover:bg-neutral-900 transition-all duration-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-md text-xs font-semibold uppercase tracking-wider cursor-pointer">
                  Quero Meu App com IA <FiArrowRight />
                </button>
              </Link>
              <a 
                href="https://wa.me/5582996992182" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-full sm:w-auto"
              >
                <button className="w-full sm:w-auto px-6 py-2.5 bg-white text-black border border-black rounded-none transition-all duration-300 flex items-center justify-center gap-2 hover:bg-black hover:text-white shadow-sm hover:shadow-md text-xs font-semibold uppercase tracking-wider cursor-pointer">
                  Falar com Especialista
                </button>
              </a>
            </div>

          </div>

        </div>

        {/* 
        Chamada para Aplicativos - Transforme seu Site em Aplicativo (Comentado temporariamente)
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-[0.01]" style={{ backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`, backgroundSize: '16px 16px' }}></div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-4 text-left">
              <span className="text-[10px] uppercase font-bold tracking-widest text-blue-600 block mb-2">Pronto para Publicar</span>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight mb-2">
                Transforme seu Site em Aplicativo
              </h3>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Nossa plataforma gera os pacotes nativos e integra a automação inteligente em uma única solução simplificada.
              </p>
            </div>
            
            <div className="lg:col-span-8">
              <div className="flex flex-wrap gap-3 justify-start lg:justify-end">
                {[
                  { name: "Android", icon: "🤖" },
                  { name: "iPhone (iOS)", icon: "🍏" },
                  { name: "Notificações Push", icon: "🔔" },
                  { name: "Painel Admin", icon: "📊" },
                  { name: "Atualizações Simples", icon: "🔄" },
                  { name: "Integração com IA", icon: "🧠" },
                  { name: "WhatsApp Integrado", icon: "💬" }
                ].map((item, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-slate-50 border border-gray-100 text-xs font-medium text-gray-700 hover:border-blue-200 hover:bg-blue-50/30 transition-all cursor-default"
                  >
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        */}

      </div>
    </section>
  );
};

export default AiFeatureSection;
